import clsx from 'clsx';
import { useMemo, Fragment } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { HighlightExtended } from '../../content/code-snippet/HighlightExtended.js';
import { getStratoLogger } from '../../core/sdk/sdk-logger.js';
import { textStyle as textStyleCSS } from '../../styles/textStyle.sty.js';

const ELLIPSIS_CHARACTER = '\u2026';
/** Default max tokenization length for log columns. */
const MAX_TOKENIZATION_LENGTH = 1000;
/** Default truncation length for log columns. */
const DEFAULT_TRUNCATION_LENGTH = MAX_TOKENIZATION_LENGTH;

/**
 * Helper function on the cell renderer that will allow to format log content.
 */
function useFormatLogContent(
  logContent: string,
  options?: { lineWrap?: boolean; truncationLimit?: number | false },
) {
  const stratoLogger = getStratoLogger('DataTable');

  return useMemo(() => {
    if (logContent !== null && logContent !== undefined) {
      const lineWrap = options?.lineWrap ?? true;
      let logContentValue = logContent;

      // If lineWrap is set to false, the log content should be in a single line.
      if (!lineWrap) {
        logContentValue = logContent.replace(/[\n\r]/g, ' ');
      }

      const truncationLimit = getTruncationLimit(options?.truncationLimit);
      const highlightIndex = Math.min(
        MAX_TOKENIZATION_LENGTH,
        truncationLimit, // in case truncation limit is lower than 1000
        logContentValue.length, // in case log line is shorter than 1000 chars
      );

      let logContentSubstrings = {
        // max. first 1000 characters are tokenized and highlighted
        highlight: logContentValue.substring(0, highlightIndex),
        // remaining characters that are neither tokenized nor highlighted to enhance performance
        noHighlight: logContentValue.substring(
          highlightIndex,
          Math.min(truncationLimit, logContentValue.length),
        ),
      };
      if (truncationLimit < logContentValue.length) {
        logContentSubstrings = ellipsize(logContentSubstrings);
      }

      const noHighlightLines =
        logContentSubstrings.noHighlight.length > 0
          ? logContentSubstrings.noHighlight.split(/\r?\n/)
          : [];

      return (
        <ErrorBoundary
          fallback={
            <span>{logContentValue.substring(0, truncationLimit)}</span>
          }
          onError={(error, info) => {
            stratoLogger.error(error.message, error, {
              logContentValue,
              info,
            });
          }}
        >
          <HighlightExtended
            language="log"
            code={logContentSubstrings.highlight}
            key={logContentSubstrings.highlight}
          >
            {({ tokens, getLineProps, getTokenProps, className, style }) => (
              <>
                {tokens.map((line, i) => {
                  const { className: lineClassName, ...remainingLineProps } =
                    getLineProps({ line });

                  return (
                    <div
                      key={i}
                      {...remainingLineProps}
                      className={clsx(
                        className,
                        textStyleCSS({ fontStyle: 'code' }),
                      )}
                      style={{ ...style, width: '100%' }}
                    >
                      {line.map((token, j) => (
                        <span key={j} {...getTokenProps({ token })} />
                      ))}
                      {
                        /* render the remaining content that is not highlighted
                           in the same `token-line` after the last token  */
                        i === tokens.length - 1
                          ? noHighlightLines.map((line, j) => (
                              <Fragment key={`${i}-${j}`}>
                                <span>{line}</span>
                                {j < noHighlightLines.length - 1 ? (
                                  <br />
                                ) : null}
                              </Fragment>
                            ))
                          : null
                      }
                    </div>
                  );
                })}
              </>
            )}
          </HighlightExtended>
        </ErrorBoundary>
      );
    }

    return null;
  }, [logContent, options?.lineWrap, options?.truncationLimit, stratoLogger]);
}

function getTruncationLimit(truncationLimit: number | false | undefined) {
  if (truncationLimit === undefined) {
    return DEFAULT_TRUNCATION_LENGTH; // no truncation specified, default to 1000
  }
  if (truncationLimit === false) {
    return Infinity; // truncation disabled by user
  }
  return truncationLimit; // explicit truncation limit specified by user
}

function ellipsize(content: { highlight: string; noHighlight: string }): {
  highlight: string;
  noHighlight: string;
} {
  let { highlight, noHighlight } = content;
  const trimmedNoHighlight = noHighlight.trimEnd();
  if (trimmedNoHighlight.length > 0) {
    noHighlight = trimmedNoHighlight + ELLIPSIS_CHARACTER;
  } else {
    const trimmedHighlight = content.highlight.trimEnd();
    if (trimmedHighlight.length > 0) {
      highlight = trimmedHighlight + ELLIPSIS_CHARACTER;
    }
  }
  return { highlight, noHighlight };
}

export function LogContentHighlight(props: {
  logContent: string;
  lineWrap?: boolean;
  truncationLimit?: number | false;
}) {
  const { logContent, lineWrap, truncationLimit } = props;

  return useFormatLogContent(logContent, { lineWrap, truncationLimit });
}
